import React, { useState, useEffect } from 'react';
import { Dialog } from '@headlessui/react';
import { XMarkIcon, Bars3Icon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

const navigation = [
  { name: 'Methodology', href: '/methodology' },
  { name: 'Power List™', href: '/power-list' },
  { name: 'Models', href: '/#models' },
  { name: 'Pricing', href: '/#contact' },
  { name: 'Contact', href: '/#contact' },
];

const aboutDropdown = [
  { name: 'About SharpeMetrix', href: '/#about' },
  { name: 'Our Team', href: '/team' },
];

const resourcesDropdown = [
  { name: 'What are TAMPs?', href: '/what-is-tamp' },
  { name: 'TAMP Comparisons', href: '/tamp-comparison' },
];

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [resourcesDropdownOpen, setResourcesDropdownOpen] = useState(false);
  const [aboutDropdownOpen, setAboutDropdownOpen] = useState(false);
  const [dropdownTimeout, setDropdownTimeout] = useState(null);
  const location = useLocation();
  const { user, logout } = useAuth();

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (dropdownTimeout) {
        clearTimeout(dropdownTimeout);
      }
    };
  }, [dropdownTimeout]);

  // Function to handle navigation
  const handleNavigation = (e, href) => {
    // If it's a section link and we're not on the landing page
    if (href.startsWith('/#') && location.pathname !== '/') {
      e.preventDefault();
      window.location.href = href; // Force a full navigation to the landing page section
    }
  };

  const handleLogout = (e) => {
    e.preventDefault();
    logout();
    window.location.href = '/';
  };

  const handleResourcesDropdownEnter = () => {
    if (dropdownTimeout) {
      clearTimeout(dropdownTimeout);
      setDropdownTimeout(null);
    }
    setAboutDropdownOpen(false); // Close other dropdown
    setResourcesDropdownOpen(true);
  };

  const handleResourcesDropdownLeave = () => {
    const timeout = setTimeout(() => {
      setResourcesDropdownOpen(false);
    }, 150); // 150ms delay
    setDropdownTimeout(timeout);
  };

  const handleAboutDropdownEnter = () => {
    if (dropdownTimeout) {
      clearTimeout(dropdownTimeout);
      setDropdownTimeout(null);
    }
    setResourcesDropdownOpen(false); // Close other dropdown
    setAboutDropdownOpen(true);
  };

  const handleAboutDropdownLeave = () => {
    const timeout = setTimeout(() => {
      setAboutDropdownOpen(false);
    }, 150); // 150ms delay
    setDropdownTimeout(timeout);
  };

  return (
    <header className="absolute inset-x-0 top-0 z-50 bg-bg-100">
      <nav aria-label="Global" className="flex items-center justify-between p-6 lg:px-8">
        <div className="flex lg:flex-1">
          <a href="https://sharpemetrix.com" className="-m-1.5 p-1.5">
            <span className="sr-only">SharpeMetrix Model Portfolios for Advisors</span>
            <img alt="SharpeMetrix" src="/logo_wordmark.svg" className="h-8 w-auto" />
          </a>
        </div>
        <div className="flex lg:hidden">
          <button type="button" onClick={() => setMobileMenuOpen(true)} className="-m-2.5 inline-flex items-center justify-center rounded-md p-2.5 text-gray-400">
            <span className="sr-only">Open main menu</span>
            <Bars3Icon aria-hidden="true" className="h-6 w-6" />
          </button>
        </div>
        <div className="hidden lg:flex lg:gap-x-12">
          {navigation.map((item) => (
            <a
              key={item.name}
              href={item.href}
              onClick={(e) => handleNavigation(e, item.href)}
              className="text-sm font-semibold leading-6 text-gray-300 hover:text-white"
            >
              {item.name}
            </a>
          ))}
          
          {/* About Dropdown */}
          <div 
            className="relative"
            onMouseEnter={handleAboutDropdownEnter}
            onMouseLeave={handleAboutDropdownLeave}
          >
            <button
              type="button"
              className="flex items-center text-sm font-semibold leading-6 text-gray-300 hover:text-white"
              onClick={() => setAboutDropdownOpen(!aboutDropdownOpen)}
            >
              About
              <ChevronDownIcon className="ml-1 h-4 w-4" aria-hidden="true" />
            </button>
            
            {aboutDropdownOpen && (
              <div className="absolute left-0 z-10 mt-1 w-56 origin-top-left rounded-md bg-bg-200 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                <div className="py-1">
                  {aboutDropdown.map((item) => (
                    <a
                      key={item.name}
                      href={item.href}
                      className="block px-4 py-2 text-sm text-text-200 hover:bg-bg-300 hover:text-text-100 transition-colors duration-150"
                      onClick={(e) => {
                        setAboutDropdownOpen(false);
                        handleNavigation(e, item.href);
                      }}
                    >
                      {item.name}
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          {/* Resources Dropdown */}
          <div 
            className="relative"
            onMouseEnter={handleResourcesDropdownEnter}
            onMouseLeave={handleResourcesDropdownLeave}
          >
            <button
              type="button"
              className="flex items-center text-sm font-semibold leading-6 text-gray-300 hover:text-white"
              onClick={() => setResourcesDropdownOpen(!resourcesDropdownOpen)}
            >
              Resources
              <ChevronDownIcon className="ml-1 h-4 w-4" aria-hidden="true" />
            </button>
            
            {resourcesDropdownOpen && (
              <div className="absolute left-0 z-10 mt-1 w-56 origin-top-left rounded-md bg-bg-200 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                <div className="py-1">
                  {resourcesDropdown.map((item) => (
                    <a
                      key={item.name}
                      href={item.href}
                      className="block px-4 py-2 text-sm text-text-200 hover:bg-bg-300 hover:text-text-100 transition-colors duration-150"
                      onClick={() => setResourcesDropdownOpen(false)}
                    >
                      {item.name}
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="hidden lg:flex lg:flex-1 lg:justify-end">
          {!user ? (
            <>
              <a href="/register" className="text-sm font-semibold leading-6 text-white mr-8">
                Start Free 30-Day Trial <span aria-hidden="true"></span>
              </a>
              <a href="/login" className="text-sm font-semibold leading-6 text-white">
                Log in <span aria-hidden="true">&rarr;</span>
              </a>
            </>
          ) : (
            <>
              <a href="/models" className="text-sm font-semibold leading-6 text-white mr-8">
                Dashboard
              </a>
              <button
                onClick={handleLogout}
                className="text-sm font-semibold leading-6 text-white"
              >
                Log out <span aria-hidden="true">&rarr;</span>
              </button>
            </>
          )}
        </div>
      </nav>
      <Dialog open={mobileMenuOpen} onClose={setMobileMenuOpen} className="lg:hidden">
        <div className="fixed inset-0 z-10" />
        <Dialog.Panel className="fixed inset-y-0 right-0 z-10 w-full overflow-y-auto bg-bg-100 px-6 py-6 sm:max-w-sm sm:ring-1 sm:ring-bg-300/10">
          <div className="flex items-center justify-between">
            <a href="https://sharpemetrix.com" className="-m-1.5 p-1.5">
              <span className="sr-only">SharpeMetrix</span>
              <img alt="SharpeMetrix" src="/logo_wordmark.svg" className="h-8 w-auto" />
            </a>
            <button type="button" onClick={() => setMobileMenuOpen(false)} className="-m-2.5 rounded-md p-2.5 text-text-200">
              <span className="sr-only">Close menu</span>
              <XMarkIcon aria-hidden="true" className="h-6 w-6" />
            </button>
          </div>
          <div className="mt-6 flow-root">
            <div className="-my-6 divide-y divide-gray-500/25">
              <div className="space-y-2 py-6">
                {navigation.map((item) => (
                  <a
                    key={item.name}
                    href={item.href}
                    onClick={(e) => handleNavigation(e, item.href)}
                    className="-mx-3 block rounded-lg px-3 py-2 text-base font-semibold leading-7 text-white hover:bg-gray-800"
                  >
                    {item.name}
                  </a>
                ))}
                
                {/* About section in mobile menu */}
                <div className="border-t border-gray-500/25 pt-4">
                  <div className="text-sm font-semibold text-gray-400 px-3 py-2">About</div>
                  {aboutDropdown.map((item) => (
                    <a
                      key={item.name}
                      href={item.href}
                      onClick={(e) => handleNavigation(e, item.href)}
                      className="-mx-3 block rounded-lg px-3 py-2 text-base font-semibold leading-7 text-white hover:bg-gray-800"
                    >
                      {item.name}
                    </a>
                  ))}
                </div>
                
                {/* Resources section in mobile menu */}
                <div className="border-t border-gray-500/25 pt-4">
                  <div className="text-sm font-semibold text-gray-400 px-3 py-2">Resources</div>
                  {resourcesDropdown.map((item) => (
                    <a
                      key={item.name}
                      href={item.href}
                      className="-mx-3 block rounded-lg px-3 py-2 text-base font-semibold leading-7 text-white hover:bg-gray-800"
                    >
                      {item.name}
                    </a>
                  ))}
                </div>
              </div>
              <div className="py-6">
                {!user ? (
                  <>
                    <a href="/register" className="-mx-3 block rounded-lg px-3 py-2.5 text-base font-semibold leading-7 text-white hover:bg-gray-800">
                      Start Free 30-Day Trial
                    </a>
                    <a href="/login" className="-mx-3 block rounded-lg px-3 py-2.5 text-base font-semibold leading-7 text-white hover:bg-gray-800">
                      Log in
                    </a>
                  </>
                ) : (
                  <>
                    <a href="/models" className="-mx-3 block rounded-lg px-3 py-2.5 text-base font-semibold leading-7 text-white hover:bg-gray-800">
                      Dashboard
                    </a>
                    <button
                      onClick={handleLogout}
                      className="-mx-3 block rounded-lg px-3 py-2.5 text-base font-semibold leading-7 text-white hover:bg-gray-800 w-full text-left"
                    >
                      Log out
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </Dialog.Panel>
      </Dialog>
    </header>
  );
} 